/**
 * «Что горит сегодня» — сердце пульта.
 * Сохранённый контракт /api/dashboard, переосмысленный как очередь действий:
 * каждая строка = действие с кнопкой. Оператор работает сверху вниз и обнуляет ленту.
 * Каждый флаг подписан суммой ₽ — срочность измеряется деньгами.
 */

import { Link } from '@tanstack/react-router'
import { AlertCircle, Clock, Inbox, UserCheck, ListChecks } from 'lucide-react'
import { useClaimLead } from '@/hooks/useLeads'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatRelativeTime, formatRub, getLeadUrgencyClass } from '@/lib/utils'
import type { Dashboard, DashboardDeal, DashboardLead, DashboardSlaFlag } from '@/types'

// ---- source channel label ----
const SOURCE_LABEL: Record<string, string> = {
  web_form: 'Сайт',
  telegram: 'Telegram',
  max: 'MAX',
  avito: 'Авито',
  manual: 'Вручную',
}

function CountPill({ count, tone }: { count: number; tone: 'red' | 'amber' | 'slate' }) {
  if (count <= 0) return null
  const bg = tone === 'red' ? 'bg-red-500' : tone === 'amber' ? 'bg-amber-500' : 'bg-slate-400'
  return (
    <span
      className={`flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-bold text-white ${bg}`}
    >
      {count}
    </span>
  )
}

// ---- Unclaimed lead row ----

function UnclaimedRow({ lead }: { lead: DashboardLead }) {
  const claim = useClaimLead()
  const urgency = getLeadUrgencyClass(lead.created_at, lead.claimed_by ?? null)
  const isRed = urgency === 'urgency-red'
  const isYellow = urgency === 'urgency-yellow'
  const name = lead.display_name ?? lead.tenant_id

  return (
    <div
      className={`flex items-center gap-3 rounded-lg border p-3 ${
        isRed ? 'border-red-200 bg-red-50' : isYellow ? 'border-amber-200 bg-amber-50' : 'bg-card'
      }`}
    >
      {isRed ? (
        <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />
      ) : (
        <Clock className="h-4 w-4 shrink-0 text-amber-500" />
      )}

      <div className="min-w-0 flex-1">
        {lead.deal_id ? (
          <Link
            to="/contact/$id"
            params={{ id: lead.deal_id }}
            className="text-sm font-medium hover:underline"
          >
            {name}
          </Link>
        ) : (
          <span className="text-sm font-medium">{name}</span>
        )}
        <div className="mt-0.5 flex items-center gap-2">
          {lead.source_channel && (
            <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
              {SOURCE_LABEL[lead.source_channel] ?? lead.source_channel}
            </Badge>
          )}
          <span className={`text-xs ${isRed ? 'font-medium text-red-600' : 'text-muted-foreground'}`}>
            {formatRelativeTime(lead.created_at)}
          </span>
        </div>
      </div>

      <Button
        variant="claim"
        size="sm"
        className="shrink-0"
        onClick={() => claim.mutate(lead.deal_id)}
        disabled={claim.isPending}
      >
        <UserCheck className="h-3.5 w-3.5" />
        Взял
      </Button>
    </div>
  )
}

// ---- Deal row (no-next-step / SLA) — money-first ----

function DealRow({ deal, sla = false }: { deal: DashboardDeal | DashboardSlaFlag; sla?: boolean }) {
  const hoursIdle = 'hours_idle' in deal ? (deal as DashboardSlaFlag).hours_idle : null
  const name = deal.display_name ?? deal.title ?? deal.tenant_id
  const stage = deal.stage_name ?? deal.stage_key
  const value = deal.value ?? null

  return (
    <div
      className={`flex items-center gap-3 rounded-lg border p-3 ${
        sla ? 'border-red-200 bg-red-50' : 'bg-card'
      }`}
    >
      {sla && <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {deal.deal_id ? (
            <Link
              to="/contact/$id"
              params={{ id: deal.deal_id }}
              className="truncate text-sm font-medium hover:underline"
            >
              {name}
            </Link>
          ) : (
            <span className="truncate text-sm font-medium">{name}</span>
          )}
          {value != null && value > 0 && (
            <span className={`shrink-0 text-sm font-semibold tabular-nums ${sla ? 'text-red-600' : 'text-foreground'}`}>
              {formatRub(value)}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {stage}
          {hoursIdle != null
            ? ` · ${Math.round(hoursIdle)}ч без движения`
            : ` · обновлено ${formatRelativeTime(deal.updated_at)}`}
        </p>
      </div>

      {deal.deal_id && (
        <Button asChild variant="outline" size="sm" className="shrink-0">
          <Link to="/contact/$id" params={{ id: deal.deal_id }}>
            {sla ? 'Открыть' : 'Назначить шаг'}
          </Link>
        </Button>
      )}
    </div>
  )
}

export function WhatsHot({ dashboard }: { dashboard: Dashboard }) {
  const {
    unclaimed_leads = [],
    no_next_step_deals = [],
    sla_flags = [],
  } = dashboard
  // самые забытые сверху
  const noStepSorted = [...no_next_step_deals].sort(
    (a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
  )

  return (
    <div className="space-y-4">
      {/* SLA — наверху очереди, во всю ширину, красная */}
      {sla_flags.length > 0 && (
        <Card id="sla-list" className="border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-red-700">
              <AlertCircle className="h-4 w-4" />
              SLA-просрочка (&gt;72ч без движения)
              <CountPill count={sla_flags.length} tone="red" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {sla_flags.map((d) => (
              <DealRow key={d.deal_id} deal={d} sla />
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Непринятые лиды */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Inbox className="h-4 w-4 text-muted-foreground" />
              Ждут «Взял»
              <CountPill count={unclaimed_leads.length} tone="red" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {unclaimed_leads.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">Все лиды обработаны</p>
            ) : (
              <>
                {unclaimed_leads.map((l) => (
                  <UnclaimedRow key={l.deal_id} lead={l} />
                ))}
                <Link to="/leads" className="block pt-1 text-xs text-primary hover:underline">
                  Все лиды →
                </Link>
              </>
            )}
          </CardContent>
        </Card>

        {/* Сделки без следующего шага */}
        <Card id="no-step-list">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <ListChecks className="h-4 w-4 text-muted-foreground" />
              Без следующего шага
              <CountPill count={no_next_step_deals.length} tone="amber" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {noStepSorted.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">У всех сделок есть шаг</p>
            ) : (
              <>
                {noStepSorted.map((d) => (
                  <DealRow key={d.deal_id} deal={d} />
                ))}
                <Link to="/pipeline" className="block pt-1 text-xs text-primary hover:underline">
                  Воронка →
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
